import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
// Force rebuild v1.0.2 - 2025-10-29
import { HelmetProvider } from 'react-helmet-async';
import logoImage from './assets/logo-black.png';
import Dashboard from './components/Dashboard';
import RestaurantManagement from './components/RestaurantManagement';
import UserManagement from './components/UserManagement';
import AdminDashboard from './components/admin/AdminDashboard';
import InventoryManagement from './pages/InventoryManagement';
import MenuItemManagement from './components/MenuItemManagement';
import CategoryManagement from './components/CategoryManagement';
import MenuItemAssignment from './components/MenuItemAssignment';
import TaxManagement from './components/TaxManagement';
import DiscountManagement from './components/DiscountManagement';
import HierarchicalPricingManagement from './components/HierarchicalPricingManagement';
import ModifierManagement from './components/ModifierManagement';
import ApprovalWorkflow from './components/ApprovalWorkflowSimulation';
import LocationOptions from './components/LocationOptions';
import SupportDashboard from './components/support/SupportDashboard';
import AIChatAssistant from './components/AIChatAssistant';
import QRMenu from './components/QRMenu';
import QRCodeGenerator from './components/QRCodeGenerator';
import { PeopleManagement } from './components/PeopleManagement';
import UserProfile from './components/UserProfile';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import { PerformanceDashboard } from './components/performance/PerformanceDashboard';
import DeviceHealthDashboard from './components/device/DeviceHealthDashboard';
import MarketingLandingPage from './pages/MarketingLandingPage';
import EnhancedMarketingLandingPage from './pages/EnhancedMarketingLandingPage';
import SEOHead from './components/SEOHead';
import { SocialMediaDashboard } from './components/social-media/SocialMediaDashboard';
import MenuBoardConfigurator from './components/MenuBoardConfigurator';
import { FacebookCallback } from './components/social-media/FacebookCallback';
import { LinkedInOAuthCallback } from './components/social-media/LinkedInOAuthCallback';
import { OAuthDebug } from './components/social-media/OAuthDebug';
import { FacebookOAuthDiagnostic } from './components/social-media/FacebookOAuthDiagnostic';
import { Login } from './pages/Login';
import RestaurantMessaging from './components/RestaurantMessaging';
import KitchenDisplaySystem from './components/KitchenDisplaySystem';
import DeliveryManagement from './components/DeliveryManagement';
import { WaitlistManagement } from './components/waitlist/WaitlistManagement';
import VoiceOrderingConfig from './components/VoiceOrderingConfig';
import VoiceOrderingSimulator from './components/VoiceOrderingSimulator';
import PhoneAssistantConfig from './components/PhoneAssistantConfig';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TermsOfService } from './components/TermsOfService';
import Revolutionary from './pages/Revolutionary';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import MicroSurvey from './pages/MicroSurvey';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ScopeProvider, useScope } from './contexts/ScopeContext';
import StoreSelector from './components/StoreSelector';
import { RequireStore } from './components/RequireStore';
import { CartProvider } from './contexts/CartContext';
import { OrderProvider } from './contexts/OrderContext';
import { PrivateRoute } from './components/PrivateRoute';
import { getUserRole, getUserDisplayName, canAccessAdmin, hasPermission } from './utils/roleUtils';
import ApiGatewayInitializer from './components/ApiGatewayInitializer';
import SecurityAuditManagement from './components/SecurityAuditManagement';
import NetworkManagement from './components/NetworkManagement';
import AlertBottomBar from './components/AlertBottomBar';
import { API_ENDPOINTS } from './config/apiRouting';
import WeatherDashboard from './components/weather/WeatherDashboard';
import PaymentCheckout from './components/payment/PaymentCheckout';
import PaymentTest from './components/payment/PaymentTest';
import NotificationManagement from './components/admin/NotificationManagement';
import EquipmentMonitoringDashboard from './components/EquipmentMonitoringDashboard';
import EquipmentDeviceDetails from './components/EquipmentDeviceDetails';
import EquipmentAnalytics from './components/EquipmentAnalytics';
import { OnboardingWizard } from './components/onboarding/OnboardingWizard';
import BillingManagement from './components/billing/BillingManagement';
import PromoCodeDashboard from './components/billing/PromoCodeDashboard';
import PricingPage from './components/billing/PricingPage';
import TableChartPage from './components/restaurant/TableChartPage';
import CommandCenterDashboard from './components/CommandCenterDashboard';
import ReviewsPage from './pages/ReviewsPage';
import RecoveryPage from './pages/RecoveryPage';
import CRMDashboard from './components/crm/CRMDashboard';
import {
  DeveloperLayout,
  DevOverview,
  DevDocs,
  DevKeys,
  DevWebhooks
} from './pages/developers';
import { DesignSystemDemo } from './pages/DesignSystemDemo';
import {
  VoiceConfigurationPanel,
  VoiceAnalyticsDashboard,
  PhoneAIConfigPanel,
  VoiceReportingWidget,
  VoiceSessionLog,
  VoiceFeedbackDashboard,
  VoiceContextInspector
} from './components/voice';
import { InventoryForecastDashboard } from './components/inventory/InventoryForecastDashboard';
import PurchaseOrdersDashboard from './components/inventory/PurchaseOrdersDashboard';
import InventoryReceiving from './components/inventory/InventoryReceiving';
import SupplierScorecard from './components/inventory/SupplierScorecard';
import InvoiceScanner from './components/inventory/InvoiceScanner';
import NetworkAlerts from './components/inventory/NetworkAlerts';
import WasteAnalytics from './components/inventory/WasteAnalytics';
import VisionZoneConfig from './components/inventory/VisionZoneConfig';
import RecipeManagement from './components/inventory/RecipeManagement';
import './App-modern.css';

const AppContent: React.FC = () => {
  const { user, session, signOut } = useAuth();
  const { storeId: scopeStoreId, enterpriseId: scopeEnterpriseId, store: scopeStore } = useScope();
  const sid = scopeStoreId || '';
  const eid = scopeEnterpriseId || '';
  const sname = scopeStore?.name || '';
  const location = useLocation();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    menu: false,
    service: false,
    orders: false,
    voice: false,
    inventory: false,
    people: false,
    marketing: false,
    finance: false,
    analytics: false,
    settings: false,
  });
  const [viewFilter, setViewFilter] = useState<string>(() => localStorage.getItem('bso-view-filter') || 'all');

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const userRole = getUserRole(user, session);
  const userName = getUserDisplayName(user, session);

  // Auto-set view filter for marketing users
  useEffect(() => {
    if (userRole === 'marketing' && viewFilter !== 'marketing_manager') {
      setViewFilter('marketing_manager');
      localStorage.setItem('bso-view-filter', 'marketing_manager');
    }
  }, [userRole]);

  const VIEW_FILTERS: Record<string, { label: string; paths: string[] | null }> = {
    all: { label: 'All Access', paths: null },
    site_manager: { label: 'Site Manager', paths: ['/locations', '/location-options', '/equipment', '/equipment-analytics', '/device-health'] },
    marketing_manager: { label: 'Marketing Manager', paths: ['/locations', '/location-options', '/social-media', '/onboarding', '/discounts', '/qr-codes', '/reviews', '/recovery', '/waitlist', '/command-center', '/table-charts'] },
  };

  const isNavVisible = (path: string): boolean => {
    const filter = VIEW_FILTERS[viewFilter];
    if (!filter || filter.paths === null) return true;
    return filter.paths.includes(path);
  };

  const isGroupVisible = (childPaths: string[]): boolean => {
    return childPaths.some(path => isNavVisible(path));
  };

  // AppContent component renders the main application interface

  return (
    <div className="App">
      {/* Sidebar Navigation */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <img 
            src={logoImage} 
            alt="BigSystemOne" 
            className="sidebar-logo"
          />
        </div>
        
        <nav className="sidebar-nav">
          <ul>
            {/* Pinned standalone items */}
            <li>
              <Link to="/dashboard" className={isActive('/dashboard') ? 'active' : ''}>
                Dashboard
              </Link>
            </li>
            {isNavVisible('/command-center') && canAccessAdmin(user, session) && (
              <li>
                <Link to="/command-center" className={isActive('/command-center') ? 'active' : ''}>
                  Command Center
                </Link>
              </li>
            )}

            {/* Menu */}
            {isGroupVisible(['/menu-items', '/categories', '/modifiers', '/menu-boards', '/menu-assignment']) && canAccessAdmin(user, session) && (
              <li className="nav-group">
                <button
                  className="nav-group-header"
                  onClick={() => toggleGroup('menu')}
                  aria-expanded={expandedGroups.menu}
                >
                  <span className="nav-group-title">Menu</span>
                  <span className="nav-group-toggle">{expandedGroups.menu ? '▼' : '▶'}</span>
                </button>
                {expandedGroups.menu && (
                  <ul className="nav-group-items">
                    {isNavVisible('/menu-items') && (
                      <li><Link to="/menu-items" className={isActive('/menu-items') ? 'active' : ''}>Menu Items</Link></li>
                    )}
                    {isNavVisible('/categories') && (
                      <li><Link to="/categories" className={isActive('/categories') ? 'active' : ''}>Categories</Link></li>
                    )}
                    {isNavVisible('/modifiers') && (
                      <li><Link to="/modifiers" className={isActive('/modifiers') ? 'active' : ''}>Modifiers</Link></li>
                    )}
                    {isNavVisible('/menu-boards') && (
                      <li><Link to="/menu-boards" className={isActive('/menu-boards') ? 'active' : ''}>Menu Boards</Link></li>
                    )}
                    {isNavVisible('/menu-assignment') && (
                      <li><Link to="/menu-assignment" className={isActive('/menu-assignment') ? 'active' : ''}>Menu Assignment</Link></li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Service — in-shift ops */}
            {isGroupVisible(['/waitlist', '/table-charts', '/kitchen-display', '/messaging', '/approvals']) && (
              <li className="nav-group">
                <button
                  className="nav-group-header"
                  onClick={() => toggleGroup('service')}
                  aria-expanded={expandedGroups.service}
                >
                  <span className="nav-group-title">Service</span>
                  <span className="nav-group-toggle">{expandedGroups.service ? '▼' : '▶'}</span>
                </button>
                {expandedGroups.service && (
                  <ul className="nav-group-items">
                    {isNavVisible('/waitlist') && (
                      <li><Link to="/waitlist" className={isActive('/waitlist') ? 'active' : ''}>Waitlist &amp; Host</Link></li>
                    )}
                    {isNavVisible('/table-charts') && (
                      <li><Link to="/table-charts" className={isActive('/table-charts') ? 'active' : ''}>Table Charts</Link></li>
                    )}
                    {isNavVisible('/kitchen-display') && canAccessAdmin(user, session) && (
                      <li><Link to="/kitchen-display" className={isActive('/kitchen-display') ? 'active' : ''}>Kitchen Display</Link></li>
                    )}
                    {isNavVisible('/messaging') && canAccessAdmin(user, session) && (
                      <li><Link to="/messaging" className={isActive('/messaging') ? 'active' : ''}>Messaging</Link></li>
                    )}
                    {isNavVisible('/approvals') && canAccessAdmin(user, session) && (
                      <li><Link to="/approvals" className={isActive('/approvals') ? 'active' : ''}>Approvals</Link></li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Orders — customer-facing order channels */}
            {isGroupVisible(['/qr-codes', '/delivery', '/voice-ordering', '/phone-assistant']) && (
              <li className="nav-group">
                <button
                  className="nav-group-header"
                  onClick={() => toggleGroup('orders')}
                  aria-expanded={expandedGroups.orders}
                >
                  <span className="nav-group-title">Orders</span>
                  <span className="nav-group-toggle">{expandedGroups.orders ? '▼' : '▶'}</span>
                </button>
                {expandedGroups.orders && (
                  <ul className="nav-group-items">
                    {isNavVisible('/qr-codes') && (
                      <li><Link to="/qr-codes" className={isActive('/qr-codes') ? 'active' : ''}>QR Code Menus</Link></li>
                    )}
                    {isNavVisible('/delivery') && canAccessAdmin(user, session) && (
                      <li><Link to="/delivery" className={isActive('/delivery') ? 'active' : ''}>Delivery</Link></li>
                    )}
                    {isNavVisible('/voice-ordering') && canAccessAdmin(user, session) && (
                      <li><Link to="/voice-ordering" className={isActive('/voice-ordering') ? 'active' : ''}>Voice Ordering</Link></li>
                    )}
                    {isNavVisible('/phone-assistant') && canAccessAdmin(user, session) && (
                      <li><Link to="/phone-assistant" className={isActive('/phone-assistant') ? 'active' : ''}>Phone AI Assistant</Link></li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Voice — voice/phone admin workbench */}
            {isGroupVisible(['/voice/config', '/voice-simulator', '/voice/sessions', '/voice/feedback', '/voice/context', '/voice/analytics', '/voice/reports', '/phone-ai/config']) && canAccessAdmin(user, session) && (
              <li className="nav-group">
                <button
                  className="nav-group-header"
                  onClick={() => toggleGroup('voice')}
                  aria-expanded={expandedGroups.voice}
                >
                  <span className="nav-group-title">Voice</span>
                  <span className="nav-group-toggle">{expandedGroups.voice ? '▼' : '▶'}</span>
                </button>
                {expandedGroups.voice && (
                  <ul className="nav-group-items">
                    {isNavVisible('/voice/config') && (
                      <li><Link to="/voice/config" className={isActive('/voice/config') ? 'active' : ''}>Configuration</Link></li>
                    )}
                    {isNavVisible('/voice-simulator') && (
                      <li><Link to="/voice-simulator" className={isActive('/voice-simulator') ? 'active' : ''}>Simulator</Link></li>
                    )}
                    {isNavVisible('/voice/sessions') && (
                      <li><Link to="/voice/sessions" className={isActive('/voice/sessions') ? 'active' : ''}>Session Log</Link></li>
                    )}
                    {isNavVisible('/voice/feedback') && (
                      <li><Link to="/voice/feedback" className={isActive('/voice/feedback') ? 'active' : ''}>Feedback</Link></li>
                    )}
                    {isNavVisible('/voice/context') && (
                      <li><Link to="/voice/context" className={isActive('/voice/context') ? 'active' : ''}>Context Inspector</Link></li>
                    )}
                    {isNavVisible('/voice/analytics') && (
                      <li><Link to="/voice/analytics" className={isActive('/voice/analytics') ? 'active' : ''}>Analytics</Link></li>
                    )}
                    {isNavVisible('/voice/reports') && (
                      <li><Link to="/voice/reports" className={isActive('/voice/reports') ? 'active' : ''}>Reports</Link></li>
                    )}
                    {isNavVisible('/phone-ai/config') && (
                      <li><Link to="/phone-ai/config" className={isActive('/phone-ai/config') ? 'active' : ''}>Phone AI (xAI)</Link></li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Inventory — single entry; the page itself tabs all sub-areas */}
            {isNavVisible('/inventory') && canAccessAdmin(user, session) && (
              <li>
                <Link to="/inventory" className={isActive('/inventory') ? 'active' : ''}>Inventory</Link>
              </li>
            )}

            {/* People */}
            {isGroupVisible(['/people', '/users']) && canAccessAdmin(user, session) && (
              <li className="nav-group">
                <button
                  className="nav-group-header"
                  onClick={() => toggleGroup('people')}
                  aria-expanded={expandedGroups.people}
                >
                  <span className="nav-group-title">People</span>
                  <span className="nav-group-toggle">{expandedGroups.people ? '▼' : '▶'}</span>
                </button>
                {expandedGroups.people && (
                  <ul className="nav-group-items">
                    {isNavVisible('/people') && (
                      <li><Link to="/people" className={isActive('/people') ? 'active' : ''}>Employees</Link></li>
                    )}
                    {isNavVisible('/users') && (
                      <li><Link to="/users" className={isActive('/users') ? 'active' : ''}>Users</Link></li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Marketing */}
            {isGroupVisible(['/reviews', '/recovery', '/social-media', '/discounts']) && (
              <li className="nav-group">
                <button
                  className="nav-group-header"
                  onClick={() => toggleGroup('marketing')}
                  aria-expanded={expandedGroups.marketing}
                >
                  <span className="nav-group-title">Marketing</span>
                  <span className="nav-group-toggle">{expandedGroups.marketing ? '▼' : '▶'}</span>
                </button>
                {expandedGroups.marketing && (
                  <ul className="nav-group-items">
                    {isNavVisible('/reviews') && (
                      <li><Link to="/reviews" className={isActive('/reviews') ? 'active' : ''}>Reviews</Link></li>
                    )}
                    {isNavVisible('/recovery') && (
                      <li><Link to="/recovery" className={isActive('/recovery') ? 'active' : ''}>Recovery</Link></li>
                    )}
                    {isNavVisible('/social-media') && (
                      <li><Link to="/social-media" className={isActive('/social-media') ? 'active' : ''}>Social Media</Link></li>
                    )}
                    {isNavVisible('/discounts') && (
                      <li><Link to="/discounts" className={isActive('/discounts') ? 'active' : ''}>Discounts</Link></li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Finance */}
            {isGroupVisible(['/pricing', '/promo-codes', '/tax-management', '/billing']) && canAccessAdmin(user, session) && (
              <li className="nav-group">
                <button
                  className="nav-group-header"
                  onClick={() => toggleGroup('finance')}
                  aria-expanded={expandedGroups.finance}
                >
                  <span className="nav-group-title">Finance</span>
                  <span className="nav-group-toggle">{expandedGroups.finance ? '▼' : '▶'}</span>
                </button>
                {expandedGroups.finance && (
                  <ul className="nav-group-items">
                    {isNavVisible('/pricing') && (
                      <li><Link to="/pricing" className={isActive('/pricing') ? 'active' : ''}>Pricing</Link></li>
                    )}
                    {isNavVisible('/promo-codes') && (
                      <li><Link to="/promo-codes" className={isActive('/promo-codes') ? 'active' : ''}>Promo Codes</Link></li>
                    )}
                    {isNavVisible('/tax-management') && (
                      <li><Link to="/tax-management" className={isActive('/tax-management') ? 'active' : ''}>Tax</Link></li>
                    )}
                    {isNavVisible('/billing') && (
                      <li><Link to="/billing" className={isActive('/billing') ? 'active' : ''}>Billing</Link></li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Analytics */}
            {isGroupVisible(['/analytics', '/performance', '/weather', '/equipment-analytics', '/crm']) && canAccessAdmin(user, session) && (
              <li className="nav-group">
                <button
                  className="nav-group-header"
                  onClick={() => toggleGroup('analytics')}
                  aria-expanded={expandedGroups.analytics}
                >
                  <span className="nav-group-title">Analytics</span>
                  <span className="nav-group-toggle">{expandedGroups.analytics ? '▼' : '▶'}</span>
                </button>
                {expandedGroups.analytics && (
                  <ul className="nav-group-items">
                    {isNavVisible('/analytics') && (
                      <li><Link to="/analytics" className={isActive('/analytics') ? 'active' : ''}>Analytics</Link></li>
                    )}
                    {isNavVisible('/performance') && (
                      <li><Link to="/performance" className={isActive('/performance') ? 'active' : ''}>Performance</Link></li>
                    )}
                    {isNavVisible('/weather') && (
                      <li><Link to="/weather" className={isActive('/weather') ? 'active' : ''}>Weather</Link></li>
                    )}
                    {isNavVisible('/equipment-analytics') && (
                      <li><Link to="/equipment-analytics" className={isActive('/equipment-analytics') ? 'active' : ''}>Equipment Analytics</Link></li>
                    )}
                    {isNavVisible('/crm') && (
                      <li><Link to="/crm" className={isActive('/crm') ? 'active' : ''}>CRM</Link></li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Settings — system/admin config */}
            {isGroupVisible(['/locations', '/location-options', '/equipment', '/network-management', '/device-health', '/notification-management', '/onboarding', '/admin', '/security-audit']) && (
              <li className="nav-group">
                <button
                  className="nav-group-header"
                  onClick={() => toggleGroup('settings')}
                  aria-expanded={expandedGroups.settings}
                >
                  <span className="nav-group-title">Settings</span>
                  <span className="nav-group-toggle">{expandedGroups.settings ? '▼' : '▶'}</span>
                </button>
                {expandedGroups.settings && (
                  <ul className="nav-group-items">
                    {isNavVisible('/locations') && (
                      <li><Link to="/locations" className={isActive('/locations') ? 'active' : ''}>Locations</Link></li>
                    )}
                    {isNavVisible('/location-options') && (
                      <li><Link to="/location-options" className={isActive('/location-options') ? 'active' : ''}>Location Options</Link></li>
                    )}
                    {isNavVisible('/equipment') && canAccessAdmin(user, session) && (
                      <li><Link to="/equipment" className={isActive('/equipment') ? 'active' : ''}>Equipment</Link></li>
                    )}
                    {isNavVisible('/network-management') && canAccessAdmin(user, session) && (
                      <li><Link to="/network-management" className={isActive('/network-management') ? 'active' : ''}>Network</Link></li>
                    )}
                    {isNavVisible('/device-health') && canAccessAdmin(user, session) && (
                      <li><Link to="/device-health" className={isActive('/device-health') ? 'active' : ''}>Device Health</Link></li>
                    )}
                    {isNavVisible('/notification-management') && canAccessAdmin(user, session) && (
                      <li><Link to="/notification-management" className={isActive('/notification-management') ? 'active' : ''}>Notifications</Link></li>
                    )}
                    {isNavVisible('/onboarding') && (
                      <li><Link to="/onboarding" className={isActive('/onboarding') ? 'active' : ''}>Review Autopilot Setup</Link></li>
                    )}
                    {isNavVisible('/admin') && canAccessAdmin(user, session) && (
                      <li><Link to="/admin" className={isActive('/admin') ? 'active' : ''}>Administration</Link></li>
                    )}
                    {isNavVisible('/security-audit') && hasPermission(user, 'security_audit_view', session) && (
                      <li><Link to="/security-audit" className={isActive('/security-audit') ? 'active' : ''}>Security Monitoring</Link></li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Support - Always at bottom */}
            <li style={{ marginTop: 'auto' }}>
              <Link to="/support" className={isActive('/support') ? 'active' : ''}>
                Support
              </Link>
            </li>
          </ul>
        </nav>
        
        <div className="sidebar-footer">
          <Link to="/profile" className={`profile-link ${isActive('/profile') ? 'active' : ''}`}>
            <div style={{ fontSize: '12px', color: '#bdc3c7', marginBottom: '8px' }}>
              {userName}
            </div>
            <div style={{ fontSize: '12px', color: '#95a5a6', marginBottom: '16px' }}>
              {userRole === 'super_admin' ? 'Super Admin' : 
               userRole === 'system_admin' ? 'System Admin' : 
               userRole === 'enterprise_admin' ? 'Enterprise Admin' : 
               userRole === 'manager' ? 'Manager' : 'Employee'}
            </div>
          </Link>
          <button onClick={signOut} className="sign-out-btn">
            Sign Out
          </button>
          <div style={{ fontSize: '10px', color: '#7f8c8d', marginTop: '12px', textAlign: 'center', lineHeight: '1.4' }}>
            &copy; Impact Restaurants, LLC. All rights reserved.
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <header className="main-header">
          <h2>
            {location.pathname === '/dashboard' && 'Dashboard'}
            {location.pathname === '/locations' && 'Location Management'}
            {location.pathname === '/location-options' && 'Location Options'}
            {location.pathname === '/users' && 'User Management'}
            {location.pathname === '/inventory' && 'Inventory Management'}
            {location.pathname === '/menu-items' && 'Menu Item Management'}
            {location.pathname === '/categories' && 'Category Management'}
            {location.pathname === '/menu-assignment' && 'Menu Item Assignment'}
            {location.pathname === '/menu-boards' && 'Digital Menu Board Configurator'}
            {location.pathname === '/people' && 'People Management'}
            {location.pathname === '/pricing' && 'Pricing Management'}
            {location.pathname === '/modifiers' && 'Modifier Management'}
            {location.pathname === '/approvals' && 'Approval Workflow'}
            {location.pathname === '/tax-management' && 'Tax Management'}
            {location.pathname === '/discounts' && 'Discount Management'}
            {location.pathname === '/social-media' && 'Social Media Management'}
            {location.pathname === '/onboarding' && 'Review Autopilot Setup'}
            {location.pathname === '/security-audit' && 'Security Auditing & Compliance'}
            {location.pathname === '/network-management' && 'Network Management'}
            {location.pathname === '/qr-codes' && 'QR Code Menu Generator'}
            {location.pathname === '/analytics' && 'Analytics Dashboard'}
            {location.pathname === '/performance' && 'Performance Dashboard'}
            {location.pathname === '/weather' && 'Weather Analytics'}
            {location.pathname === '/equipment' && 'Equipment Management'}
            {location.pathname === '/equipment-analytics' && 'Equipment Analytics'}
            {location.pathname === '/device-health' && 'Device Health Monitoring'}
            {location.pathname === '/support' && 'Support & Documentation'}
            {location.pathname === '/admin' && 'Administration'}
            {location.pathname === '/profile' && 'My Profile'}
            {location.pathname === '/messaging' && 'Restaurant Messaging'}
            {location.pathname === '/kitchen-display' && 'Kitchen Display System'}
            {location.pathname === '/waitlist' && 'Waitlist & Host Management'}
            {location.pathname === '/table-charts' && 'Table Chart Management'}
            {location.pathname === '/delivery' && 'Delivery Management'}
            {location.pathname === '/voice-ordering' && 'Voice Ordering Configuration'}
            {location.pathname === '/voice-simulator' && 'Voice Ordering Simulator'}
            {location.pathname === '/phone-assistant' && 'Phone AI Assistant'}
            {location.pathname === '/billing' && 'Billing Management'}
            {location.pathname === '/promo-codes' && 'Promo Code Management'}
            {location.pathname === '/plans' && 'Subscription Plans'}
            {location.pathname === '/crm' && 'CRM'}
          </h2>
          
          <div className="user-info">
            <StoreSelector />
            {userRole === 'super_admin' && (
              <select
                className="view-filter-select"
                value={viewFilter}
                onChange={(e) => {
                  setViewFilter(e.target.value);
                  localStorage.setItem('bso-view-filter', e.target.value);
                }}
              >
                {Object.entries(VIEW_FILTERS).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            )}
            <span>Welcome, {userName}</span>
          </div>
        </header>
        
        <div className="main-body">
          <Routes>
            <Route path="/dashboard" element={<Dashboard user={user} />} />
            <Route path="/locations" element={<RestaurantManagement />} />
            <Route path="/location-options" element={<LocationOptions />} />
            <Route path="/users" element={<UserManagement user={user} />} />
            <Route path="/inventory" element={<InventoryManagement user={user} />} />
            <Route path="/menu-items" element={<MenuItemManagement user={user} />} />
            <Route path="/categories" element={<CategoryManagement user={user} />} />
            <Route path="/menu-assignment" element={<MenuItemAssignment user={user} />} />
            <Route path="/menu-boards" element={<MenuBoardConfigurator enterpriseId={eid} />} />
            <Route path="/people" element={
              <PeopleManagement
                enterpriseId={eid}
                storeId={sid}
                user={user}
              />
            } />
            <Route path="/pricing" element={<HierarchicalPricingManagement />} />
            <Route path="/modifiers" element={<ModifierManagement user={user} />} />
            <Route path="/approvals" element={<ApprovalWorkflow user={user} />} />
            <Route path="/tax-management" element={<TaxManagement user={user} />} />
            <Route path="/discounts" element={<DiscountManagement user={user} />} />
            <Route path="/social-media" element={<SocialMediaDashboard />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/recovery" element={<RecoveryPage />} />
            <Route path="/onboarding" element={<OnboardingWizard />} />
            <Route path="/security-audit" element={<SecurityAuditManagement user={user} />} />
            <Route path="/network-management" element={<NetworkManagement />} />
            <Route path="/qr-codes" element={<QRCodeGenerator />} />
            <Route path="/analytics" element={<AnalyticsDashboard user={user} />} />
            <Route path="/command-center" element={<CommandCenterDashboard />} />
            <Route path="/performance" element={<RequireStore><PerformanceDashboard storeId={sid} enterpriseId={eid} /></RequireStore>} />
            <Route path="/weather" element={<WeatherDashboard />} />
            <Route path="/equipment/:enterpriseId/:deviceId" element={<EquipmentDeviceDetails />} />
            <Route path="/equipment" element={<EquipmentMonitoringDashboard />} />
            <Route path="/equipment-analytics" element={<EquipmentAnalytics />} />
            <Route path="/device-health" element={<DeviceHealthDashboard user={user} />} />
            <Route path="/support" element={<SupportDashboard />} />
            <Route path="/admin" element={<AdminDashboard user={user} />} />
            <Route path="/profile" element={<UserProfile user={user} />} />
            <Route path="/messaging" element={<RestaurantMessaging user={user} />} />
            <Route path="/kitchen-display" element={<KitchenDisplaySystem user={user} />} />
            <Route path="/waitlist" element={<WaitlistManagement />} />
            <Route path="/table-charts" element={<TableChartPage />} />
            <Route path="/delivery" element={<RequireStore><DeliveryManagement user={user} storeId={sid} /></RequireStore>} />
            <Route path="/notification-management" element={<NotificationManagement user={user} />} />
            <Route path="/voice-ordering" element={<RequireStore label="Voice ordering is configured per store."><VoiceOrderingConfig storeId={sid} storeName={sname} /></RequireStore>} />
            <Route path="/voice-simulator" element={<RequireStore><VoiceOrderingSimulator storeId={sid} /></RequireStore>} />
            <Route path="/phone-assistant" element={<RequireStore label="The phone assistant runs per store."><PhoneAssistantConfig storeId={sid} storeName={sname} /></RequireStore>} />
            <Route path="/voice/config" element={<RequireStore><VoiceConfigurationPanel storeId={sid} /></RequireStore>} />
            <Route path="/voice/analytics" element={<RequireStore><VoiceAnalyticsDashboard storeId={sid} /></RequireStore>} />
            <Route path="/voice/reports" element={<RequireStore><VoiceReportingWidget storeId={sid} /></RequireStore>} />
            <Route path="/voice/sessions" element={<RequireStore><VoiceSessionLog storeId={sid} /></RequireStore>} />
            <Route path="/voice/feedback" element={<RequireStore><VoiceFeedbackDashboard storeId={sid} /></RequireStore>} />
            <Route path="/voice/context" element={<RequireStore><VoiceContextInspector storeId={sid} /></RequireStore>} />
            <Route path="/inventory/forecast" element={<RequireStore label="Forecasts are store-specific."><InventoryForecastDashboard storeId={sid} /></RequireStore>} />
            <Route path="/inventory/orders" element={<RequireStore><PurchaseOrdersDashboard selectedStore={sid} enterpriseId={eid} /></RequireStore>} />
            <Route path="/inventory/receiving" element={<RequireStore><InventoryReceiving selectedStore={sid} enterpriseId={eid} user={user} /></RequireStore>} />
            <Route path="/inventory/invoices" element={<RequireStore><InvoiceScanner selectedStore={sid} enterpriseId={eid} /></RequireStore>} />
            <Route path="/inventory/suppliers" element={<RequireStore><SupplierScorecard enterpriseId={eid} selectedStore={sid} /></RequireStore>} />
            <Route path="/inventory/network" element={<RequireStore><NetworkAlerts enterpriseId={eid} selectedStore={sid} /></RequireStore>} />
            <Route path="/inventory/waste" element={<RequireStore><WasteAnalytics selectedStore={sid} enterpriseId={eid} /></RequireStore>} />
            <Route path="/inventory/vision-zones" element={<RequireStore><VisionZoneConfig selectedStore={sid} enterpriseId={eid} /></RequireStore>} />
            <Route path="/inventory/recipes" element={<RequireStore><RecipeManagement enterpriseId={eid} selectedStore={sid} /></RequireStore>} />
            <Route path="/phone-ai/config" element={<RequireStore><PhoneAIConfigPanel storeId={sid} /></RequireStore>} />
            <Route path="/billing" element={<BillingManagement user={user} />} />
            <Route path="/promo-codes" element={<PromoCodeDashboard user={user} />} />
            <Route path="/plans" element={<PricingPage />} />
            <Route path="/crm" element={<CRMDashboard />} />
          </Routes>
        </div>
      </main>

      {/* Alert Bottom Bar for Restaurant Messaging */}
      <AlertBottomBar
        userId={user?.getUsername() || 'unknown-user'}
        storeId={sid}
        role={userRole || 'employee'}
        deviceType="web"
        websocketUrl={API_ENDPOINTS.messaging.websocketUrl}
        onAlertAction={(action, alert) => {
          console.log('Alert action:', action, alert);
          // TODO: Handle alert actions (approve, deny, view details, etc.)
        }}
      />

      {/* AI Chat Button */}
      <button
        onClick={() => setIsChatOpen(true)}
        className="ai-chat-fab"
        title="AI Assistant"
        style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: '2px solid #fff',
          color: 'white',
          fontSize: '1.5rem',
          cursor: 'pointer',
          zIndex: 99999,
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L13.09 8.26L19 7L18.74 13.74L15 12L13.09 15.74L12 22L10.91 15.74L9 12L5.26 13.74L5 7L10.91 8.26L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      </button>

      {/* AI Chat Assistant */}
      <AIChatAssistant 
        isOpen={isChatOpen} 
        onClose={() => setIsChatOpen(false)} 
      />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <SEOHead />
      <AuthProvider>
        <ApiGatewayInitializer>
          <Router>
            <Routes>
              <Route path="/" element={<EnhancedMarketingLandingPage />} />
              <Route path="/marketing" element={<EnhancedMarketingLandingPage />} />
              <Route path="/old-marketing" element={<MarketingLandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/revolutionary" element={<Revolutionary />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/social-media/facebook-callback" element={<FacebookCallback />} />
              <Route path="/social-media/linkedin/callback" element={<LinkedInOAuthCallback />} />
              <Route path="/oauth-debug" element={<OAuthDebug />} />
              <Route path="/facebook-diagnostic" element={<FacebookOAuthDiagnostic />} />
              <Route path="/s/store/:storeId" element={<MicroSurvey mode="store" />} />
              <Route path="/s/:requestId" element={<MicroSurvey mode="request" />} />
              <Route path="/menu/:storeCode" element={<CartProvider><OrderProvider><QRMenu /></OrderProvider></CartProvider>} />
              <Route path="/checkout/:paymentId" element={<PaymentCheckout />} />
              <Route path="/pay/:paymentId" element={<PaymentCheckout />} />
              <Route path="/payment-test" element={<PaymentTest />} />
              <Route path="/developers" element={<DeveloperLayout />}>
                <Route index element={<DevOverview />} />
                <Route path="docs" element={<DevDocs />} />
                <Route path="keys" element={<DevKeys />} />
                <Route path="webhooks" element={<DevWebhooks />} />
              </Route>
              <Route path="/design-system" element={<DesignSystemDemo />} />
              <Route path="/*" element={
                <PrivateRoute>
                  <ScopeProvider>
                    <AppContent />
                  </ScopeProvider>
                </PrivateRoute>
              } />
            </Routes>
          </Router>
        </ApiGatewayInitializer>
      </AuthProvider>
    </HelmetProvider>
  );
};

export default App;